import { styled } from '@mui/material/styles';
import TextField, { TextFieldProps } from '@mui/material/TextField';
import { OutlinedInputProps } from '@mui/material/OutlinedInput';
import { forwardRef, KeyboardEvent, FormEvent, useRef, ReactNode } from 'react';

import { InputAdornment } from '@mui/material';
import { formatToId } from '../../utils/helper';
import { NUMBER, TEXT } from '../constants/components';
import { COLOR } from '../Theme/Colors';

const DEFAULT_PROPS: TextFieldProps = {
  variant: 'filled',
  size: 'small',
};

const CustomMuiTextField = styled(({ slotProps, ...props }: TextFieldProps) => (
  <TextField
    slotProps={{
      input: { disableUnderline: true, ...slotProps?.input } as Partial<OutlinedInputProps>,
    }}
    {...props}
    {...DEFAULT_PROPS}
  />
))(({ theme }) => ({
  label: {
    position: 'relative',
    color: COLOR.GENERIC[900],
    textAlign: 'start',
    width: '100%',
    transformOrigin: 'unset',
    transform: 'unset',
    fontSize: '14.71px',
    paddingBottom: '2px',
  },
  '& .MuiFilledInput-root': {
    backgroundColor: COLOR.GENERIC[0],
    boxShadow: `${COLOR.GENERIC[200]} 0 0 0 1px`,
    overflow: 'hidden',
    borderRadius: 8,
    input: {
      paddingTop: 12,
      paddingBottom: 12,
    },
    transition: theme.transitions.create(['border-color', 'background-color', 'box-shadow']),
    '&:hover': {
      boxShadow: `${COLOR.PRIMARY[200]} 0 0 0 1px`,
      borderColor: COLOR.PRIMARY[200],
    },
    '&.Mui-focused': {
      backgroundColor: COLOR.GENERIC[0],
      boxShadow: `${COLOR.PRIMARY[200]} 0 0 0 4px`,
      borderColor: COLOR.PRIMARY[200],
    },
    // '&.Mui-error:not(.Mui-focused)': INPUT_FIELD.ERROR_NOT_FOCUS_STYLE,
  },
  '& .MuiFormHelperText-root': {
    // ...HELPER_TEXT.STYLE,
  },
  '& .MuiFormHelperText-root.Mui-disabled': {
    backgroundColor: 'transparent',
  },
  '& .MuiFormHelperText-root.Mui-disabled:hover': {
    backgroundColor: 'transparent',
  },
  '& .MuiFormLabel-root:not(.Mui-error)': {
    // ...INPUT_LABEL.STYLE,
    color: COLOR.GENERIC[900],
  },
  '& .Mui-disabled': {
    '&:hover': {
      // backgroundColor: COLOR.NEUTRAL[200],
    },
    // backgroundColor: COLOR.NEUTRAL[200],
  },
}));

type IMuiTextField = {
  label: string;
  allowNegative?: boolean;
  maxLength?: number;
  errorText?: string;
  startAdornment?: ReactNode;
  endAdornment?: ReactNode;
} & TextFieldProps;

const MuiTextField = forwardRef((props: IMuiTextField, ref) => {
  const {
    label,
    id,
    name,
    inputProps,
    allowNegative,
    maxLength,
    errorText,
    error,
    helperText,
    InputLabelProps,
    InputProps,
    startAdornment,
    endAdornment,
    ...cleanProps
  } = props;
  const _id = formatToId(id || String(label));

  const handleOnKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.currentTarget.type === 'number' && !allowNegative)
      (NUMBER.KEYS.PREVENTS as ReadonlyArray<string>).includes(e.key) && e.preventDefault();
  };

  const handleOnInput = (e: FormEvent<HTMLInputElement>) => {
    const len = e.currentTarget.value.length;
    const maxLen = maxLength || NUMBER.LIMIT.SHORT;
    if (e.currentTarget.type === 'number' && len > maxLen) {
      e.currentTarget.value = e.currentTarget.value.slice(0, maxLen);
    }
  };

  const hasError = error ?? Boolean(errorText);

  const _ref = useRef<HTMLInputElement>();

  return (
    <CustomMuiTextField
      id={_id}
      name={name || _id}
      label={label}
      inputRef={ref ?? _ref}
      inputProps={{
        max: props.type === 'date' ? '9999-12-31' : undefined,
        maxLength: maxLength || TEXT.MAX.NAME,
        ...inputProps,
        onKeyDown: handleOnKeyDown,
        onInput: handleOnInput,
      }}
      onClick={() => {
        if (cleanProps.type === 'date' || cleanProps.type === 'datetime-local' || cleanProps.type === 'time') {
          _ref?.current?.showPicker();
        }
      }}
      error={hasError}
      helperText={helperText ?? errorText}
      slotProps={{
        input: {
          startAdornment: startAdornment ? <InputAdornment position="start">{startAdornment}</InputAdornment> : undefined,
          endAdornment: endAdornment ? <InputAdornment position="end">{endAdornment}</InputAdornment> : undefined,
          ...InputProps,
        },
      }}
      {...cleanProps}
      InputLabelProps={{
        shrink: true,
        ...InputLabelProps,
      }}
    />
  );
});
MuiTextField.displayName = 'MuiTextField';

export default MuiTextField;
